
import React, { useState, Suspense, useContext, useEffect, useRef } from "react";
import Modal from "../common/Modal";

import SocketContext from "../../context/SocketContext";
import GameRoomContext from "../../context/GameRoomContext";
import GAME_COMPONENTS from "../games";
import computeReadyInfo from './readyHelpers';
import ReadyControl from './ReadyControl';
import PlayerReadyList from './PlayerReadyList';

function ConfigUrlModal({ show, onClose, configUrl, copied, onCopy }) {
  return (
    <Modal show={show} title="Launch Link" onClose={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full max-w-2xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <input
            type="text"
            value={configUrl}
            readOnly
            className="w-full px-2 py-1 border rounded bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-100 text-sm"
            onFocus={(e) => e.target.select()}
          />
          <button
            className="px-2 py-1 rounded bg-blue-500 hover:bg-blue-600 text-white text-xs font-semibold"
            onClick={onCopy}
          >
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Anyone with this link can launch this game using the current setup options.
        </div>
      </div>
    </Modal>
  );
}



// Utility to check if a component is a React.lazy component
function isLazyComponent(Component) {
  return (
    typeof Component === "object" &&
    Component !== null &&
    Component.$$typeof === Symbol.for("react.lazy")
  );
}

// Simple modal to lookup items (supports lookupType 'artist' via Deezer search)
function LookupModal({ open, lookupType, onClose, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setResults([]);
  }, [open]);

  useEffect(() => {
    let active = true;
    if (!open) return;
    const run = async () => {
      setLoading(true);
      try {
        // If user has typed a query, perform a search; otherwise fetch a default/popular list
        if (lookupType === 'artist') {
          let data = null;
          if (query && query.trim().length > 0) {
            const url = `${process.env.REACT_APP_SOCKET_URL}/api/lookup?type=artist&q=${encodeURIComponent(query)}`;
            const res = await fetch(url);
            const payload = await res.json();
            data = payload;
          } else {
            // No query: fetch chart/top artists via server proxy to populate the initial list
            const url = `${process.env.REACT_APP_SOCKET_URL}/api/lookup?type=artist`;
            const res = await fetch(url);
            const payload = await res.json();
            data = payload;
          }
          if (!active) return;
          const items = (data && data.items) ? data.items.map(a => ({ id: a.id, name: a.name, picture: a.picture })) : (data && data.data) ? data.data.map(a => ({ id: a.id, name: a.name })) : [];
          setResults(items);
        } else {
          // other lookup types can be added here
          setResults([]);
        }
      } catch (e) {
        setResults([]);
      } finally {
        if (active) setLoading(false);
      }
    };
    // Run immediately on open and whenever query changes
    run();
    return () => { active = false; };
  }, [open, query, lookupType]);

  return (
    <Modal show={open} onClose={onClose} title={`Lookup ${lookupType}`}>
      <div className="p-4" style={{ width: '90vw', maxWidth: 480 } }>
        <div className="mb-2 flex gap-2">
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder={`Search ${lookupType}`} className="border p-2 flex-1 mr-2 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 placeholder-gray-500 dark:placeholder-gray-400" />
          <button onClick={() => setQuery(query)} className="px-2 py-1 bg-blue-500 text-white rounded">Search</button>
        </div>
        <div style={{ maxHeight: '40vh', overflow: 'auto' }}>
          {loading ? (
            <div className="text-sm text-gray-500">Loading...</div>
          ) : results.length === 0 ? (
            <div className="text-sm text-gray-500">No results</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {results.map(r => (
                <div
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(r)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(r); }}
                  className="flex flex-col items-center gap-2 p-2 rounded border border-gray-100 dark:border-gray-700 hover:shadow hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer"
                >
                  <div className="w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded overflow-hidden flex items-center justify-center">
                    {r.picture ? (
                      <img src={r.picture} alt={r.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <div className="text-sm text-gray-500">No image</div>
                    )}
                  </div>
                  <div className="text-sm text-center text-gray-800 dark:text-gray-200 truncate" style={{ maxWidth: 96 }}>{r.name}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

function ConfigOptionPickerModal({ open, title, options, selectedValue, onClose, onSelect }) {
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setQuery("");
  }, [open]);

  const normalizedQuery = query.trim().toLowerCase();
  const filteredOptions = Array.isArray(options)
    ? options.filter((option) => {
        const label = String(option.label ?? option.value ?? "").toLowerCase();
        return !normalizedQuery || label.includes(normalizedQuery);
      })
    : [];

  return (
    <Modal show={open} onClose={onClose} title={title}>
      <div className="w-full max-w-xl p-4 bg-white dark:bg-gray-800">
        {Array.isArray(options) && options.length > 8 && (
          <div className="mb-3">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search options"
              className="w-full border border-gray-300 dark:border-gray-600 px-3 py-2 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
        )}

        <div className="flex flex-col gap-2 max-h-[55vh] overflow-auto pr-1">
          {filteredOptions.length === 0 ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">No options found.</div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = selectedValue === option.value;
              return (
                <button
                  key={`${title}-${String(option.value)}`}
                  type="button"
                  className={`w-full rounded border px-3 py-3 text-left text-sm transition ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/40 dark:text-blue-200"
                      : "border-gray-300 bg-white text-gray-900 hover:border-blue-300 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:border-blue-500 dark:hover:bg-gray-600"
                  }`}
                  onClick={() => {
                    onSelect(option.value);
                    onClose();
                  }}
                >
                  <div className="font-medium">{option.label}</div>
                  {isSelected && (
                    <div className="mt-1 text-xs uppercase tracking-wide opacity-80">Selected</div>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
}

function PickerTriggerButton({ id, label, onClick, minWidth = 160, maxWidth = 220 }) {
  return (
    <button
      id={id}
      type="button"
      onClick={onClick}
      className="group flex items-center justify-between gap-3 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 shadow-sm transition hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:border-blue-500 dark:hover:bg-gray-600"
      style={{ minWidth, maxWidth, height: 34 }}
    >
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition group-hover:bg-blue-100 group-hover:text-blue-600 dark:bg-gray-600 dark:text-gray-300 dark:group-hover:bg-blue-900/50 dark:group-hover:text-blue-200">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20l-3.5-3.5" />
        </svg>
      </span>
    </button>
  );
}

function GameRoomConfig({ preservedView = false, onClosePreservedView = null }) {
  const socket = useContext(SocketContext);
  const {
    gameRoomData,
    onTogglePlayerReady,
    onUpdateGameConfig,
    onStartGame,
  } = useContext(GameRoomContext);

  const [gameRoomConfigSchema, setGameRoomConfigSchema] = useState(gameRoomData?.gameRoomConfigSchema || {});
  const [gameRoomConfig, setGameRoomConfig] = useState(gameRoomData?.gameRoomConfig || {});
  const [gameRoomPlayers, setGameRoomPlayers] = useState(Array.isArray(gameRoomData?.gameRoomPlayers) ? gameRoomData.gameRoomPlayers : []);
  // spectators are not needed in this component's local state
  const [gameRoomGame, setGameRoomGame] = useState(gameRoomData?.gameRoomGame || {});

  const lastSyncedConfigRef = useRef(gameRoomData?.gameRoomConfig || {});


    // Track which config descriptions are open
  const [openDescriptions, setOpenDescriptions] = useState({});
  // Lookup modal state
  const [lookupField, setLookupField] = useState(null); // { key, lookupType }
  const [optionPickerField, setOptionPickerField] = useState(null);
  const [showConfigUrlModal, setShowConfigUrlModal] = useState(false);
  const [configUrlCopied, setConfigUrlCopied] = useState(false);
  const handleToggleDescription = (key) => {
    setOpenDescriptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getOptionLabel = (field, value) => {
    if (field?.type === "boolean") return value ? "Yes" : "No";
    if (!Array.isArray(field?.options)) return value;
    const selectedOption = field.options.find((option) => (option.value ?? option) === value);
    return selectedOption ? (selectedOption.label ?? selectedOption.value ?? selectedOption) : value;
  };

  const buildPickerOptions = (field) => {
    if (field?.type === "boolean") {
      return [
        { value: true, label: "Yes" },
        { value: false, label: "No" },
      ];
    }
    if (!Array.isArray(field?.options)) return [];
    return field.options.map((option) => ({
      value: option.value ?? option,
      label: option.label ?? String(option.value ?? option),
    }));
  };

  const openOptionPicker = (field) => {
    setOptionPickerField({
      key: field.key,
      label: field.label,
      options: buildPickerOptions(field),
      selectedValue: gameRoomConfig[field.key],
    });
  };

  const handleOptionPickerSelect = (value) => {
    if (!optionPickerField) return;
    const newCfg = {
      ...gameRoomConfig,
      [optionPickerField.key]: value,
    };
    setGameRoomConfig(newCfg);
    onUpdateGameConfig && onUpdateGameConfig(newCfg);
  };

  useEffect(() => {
    setGameRoomConfigSchema(gameRoomData?.gameRoomConfigSchema || {});
    setGameRoomConfig(gameRoomData?.gameRoomConfig || {});
    setGameRoomPlayers(Array.isArray(gameRoomData?.gameRoomPlayers) ? gameRoomData.gameRoomPlayers : []);
    setGameRoomGame(gameRoomData?.gameRoomGame || {});
    lastSyncedConfigRef.current = gameRoomData?.gameRoomConfig || {};
  }, [gameRoomData]);

  const minPlayers = typeof gameRoomConfigSchema?.minPlayers === "number" ? gameRoomConfigSchema.minPlayers : 2;
  const maxPlayers = typeof gameRoomConfigSchema?.maxPlayers === "number" ? gameRoomConfigSchema.maxPlayers : Infinity;
  const requireReady = typeof gameRoomConfigSchema?.playersMustBeReadyToStart === "boolean"
    ? gameRoomConfigSchema.playersMustBeReadyToStart
    : true;

  const GameComponent = GAME_COMPONENTS[gameRoomConfigSchema?.gameClass];
  const GuideComponent = GameComponent?.Guide;

  const [tab, setTab] = useState(0);
  const readyInfo = computeReadyInfo(gameRoomData, socket, null);
  const {
    nonHostPlayers,
    isHost,
    isSpectator,
    readyCount,
    allReady,
    canHostStartSolo,
    isReady,
  } = readyInfo;

  // Debounce update
  const DEBOUNCE_MS = 400;
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    const hasChanged = JSON.stringify(lastSyncedConfigRef.current) !== JSON.stringify(gameRoomConfig);
    if (!hasChanged) return;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (typeof onUpdateGameConfig === "function") {
        onUpdateGameConfig(gameRoomConfig);
        lastSyncedConfigRef.current = gameRoomConfig;
      }
      debounceTimerRef.current = null;
    }, DEBOUNCE_MS);
    return () => { if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current); };
  }, [gameRoomConfig, onUpdateGameConfig]);

  // Min/max error
  let minMaxError = null;
  if (gameRoomPlayers.length < minPlayers) {
    minMaxError = `Need at least ${minPlayers} players.`;
  } else if (gameRoomPlayers.length > maxPlayers) {
    minMaxError = `Too many players (max ${maxPlayers}).`;
  }

  const playersRangeText = (min, max) => {
    if (min === max) return `${min}`;
    if (!isFinite(max)) return `${min}+`;
    return `${min} - ${max}`;
  };

  const buildConfigUrl = () => {
    const gameClass = gameRoomConfigSchema?.gameClass;
    if (!gameClass || typeof window === 'undefined') return '';

    const query = new URLSearchParams();
    Object.entries(gameRoomConfig || {}).forEach(([key, value]) => {
      if (typeof value === 'undefined' || value === null) return;
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (typeof entry !== 'undefined' && entry !== null && entry !== '') {
            query.append(key, String(entry));
          }
        });
        return;
      }
      if (value === '') return;
      query.set(key, String(value));
    });

    const queryString = query.toString();
    const launchPath = `/launch/${encodeURIComponent(gameClass)}`;
    return `${window.location.origin}${launchPath}${queryString ? `?${queryString}` : ''}`;
  };

  const configUrl = buildConfigUrl();

  const handleCopyConfigUrl = async () => {
    if (!configUrl) return;
    try {
      await navigator.clipboard.writeText(configUrl);
      setConfigUrlCopied(true);
      setTimeout(() => setConfigUrlCopied(false), 1200);
    } catch (e) {}
  };

  // Settings summary for non-hosts
  let settingsSummary = null;
  if (tab === 0 && !isHost && Array.isArray(gameRoomConfigSchema.fields)) {
    const visibleFields = gameRoomConfigSchema.fields.filter((field) => !field.hidden);
    if (visibleFields.length === 0) {
      settingsSummary = (
        <div className="w-full mb-2 text-gray-500 dark:text-gray-400 text-sm">No setup required</div>
      );
    } else {
      settingsSummary = (
        <div className="w-full mb-2">
          <div className="font-semibold text-blue-600 dark:text-blue-300 mb-1 text-sm tracking-wide uppercase">Game Settings (Host Edit Only)</div>
          <ul>
            {visibleFields.map((field) => {
              let value = gameRoomConfig[field.key];
              if (field.type === "boolean") value = value ? "Yes" : "No";
              if (field.type === "color") {
                const hex = gameRoomConfig[field.key] || "#ff0000";
                value = (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 16, height: 12, backgroundColor: hex, border: '1px solid rgba(0,0,0,0.12)', display: 'inline-block' }} />
                    <span>{hex}</span>
                  </span>
                );
              }
              if (field.type === "checkbox-list" && field.options && Array.isArray(field.options)) {
                const selected = Array.isArray(gameRoomConfig[field.key]) ? gameRoomConfig[field.key] : [];
                if (selected.length === 0) {
                  value = <span className="italic text-gray-400 dark:text-gray-500">None selected</span>;
                } else {
                  value = field.options
                    .filter(opt => selected.includes(opt.value))
                    .map(opt => opt.label ?? opt.value)
                    .join(", ");
                }
              } else if (field.options && Array.isArray(field.options) && field.type !== "checkbox-list") {
                const opt = field.options.find((o) => (o.value ?? o) === value);
                value = opt ? (opt.label ?? opt) : value;
              }
              return (
                <li key={field.key} className="flex items-top gap-2 py-1 text-sm">
                  <span className="w-52 text-gray-700 dark:text-gray-200 whitespace-nowrap">{field.label}:</span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {value}
                    {field.units && value !== undefined && value !== "" && (
                      <span className="ml-1 text-gray-500 dark:text-gray-400 whitespace-nowrap">{field.units}</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      );
    }
  }

  // Host form
  let hostForm = null;
  if (tab === 0 && isHost) {
    const visibleFields = Array.isArray(gameRoomConfigSchema.fields)
      ? gameRoomConfigSchema.fields.filter((field) => !field.hidden)
      : [];
    if (visibleFields.length === 0) {
      hostForm = (
        <div className="w-full mb-2 text-gray-500 dark:text-gray-400 text-sm">No setup required</div>
      );
    } else {
      hostForm = (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (isHost && (requireReady ? allReady : true) && gameRoomPlayers.length >= minPlayers)
              onStartGame && onStartGame();
          }}
          className="flex flex-col gap-3 items-start"
        >
          {(() => {
            // Group fields by section
            const grouped = {};
            visibleFields.forEach((field) => {
              const section = field.section || "General";
              if (!grouped[section]) grouped[section] = [];
              grouped[section].push(field);
            });
            return Object.entries(grouped).map(([section, fields]) => (
              <div key={section} className="mb-1 w-full">
                <div className="font-semibold text-blue-600 dark:text-blue-300 mb-0.5 text-sm tracking-wide uppercase">
                  {section}
                </div>
                {fields.map((field) => {
                  const id = `cfg-${field.key}`;
                  return (
                    <div key={field.key} className="grid grid-cols-[7rem_minmax(0,1fr)] items-start gap-x-3 gap-y-1 text-sm mb-2">
                      <div className="break-words min-w-0 flex flex-col">
                        <span className="flex flex-row flex-wrap items-center text-gray-700 dark:text-gray-200">
                          <label htmlFor={id} className="whitespace-normal break-words">
                            {field.label}:
                          </label>
                          {field.description && (
                            <button
                              type="button"
                              aria-label="Show info"
                              className="ml-1 text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-400 focus:outline-none"
                              style={{ fontSize: '1em', border: 'none', background: 'none', cursor: 'pointer', verticalAlign: 'middle' }}
                              onClick={() => handleToggleDescription(field.key)}
                            >
                              <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>ⓘ</span>
                            </button>
                          )}
                        </span>
                        {openDescriptions[field.key] && field.description && (
                          <div
                            className="mt-1 px-3 py-2 rounded bg-blue-50 dark:bg-blue-900 text-blue-700 dark:text-blue-200 text-xs shadow border border-blue-200 dark:border-blue-700 cursor-pointer"
                            style={{ minWidth: 180, maxWidth: 320 }}
                            onClick={() => handleToggleDescription(field.key)}
                            title="Click to hide"
                          >
                            {field.description}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex items-center gap-2 flex-wrap">
                        {field.type === "boolean" ? (
                          <>
                            <PickerTriggerButton
                              id={id}
                              label={getOptionLabel(field, !!gameRoomConfig[field.key])}
                              onClick={() => openOptionPicker(field)}
                              minWidth={160}
                              maxWidth={220}
                            />
                            {field.units && (
                              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{field.units}</span>
                            )}
                          </>
                        ) : field.options && Array.isArray(field.options) && field.type !== "checkbox-list" ? (
                          <>
                            <PickerTriggerButton
                              id={id}
                              label={getOptionLabel(field, gameRoomConfig[field.key])}
                              onClick={() => openOptionPicker(field)}
                              minWidth={140}
                              maxWidth={220}
                            />
                            {field.units && (
                              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{field.units}</span>
                            )}
                          </>
                        ) : field.type === 'color' ? (
                          <>
                            <input
                              id={id}
                              type="color"
                              value={gameRoomConfig[field.key] || '#ff0000'}
                              onChange={(e) => {
                                const val = e.target.value;
                                const newCfg = { ...gameRoomConfig, [field.key]: val };
                                setGameRoomConfig(newCfg);
                                onUpdateGameConfig && onUpdateGameConfig(newCfg);
                              }}
                              className="border border-gray-300 dark:border-gray-600 p-0.5 rounded w-12 h-8 focus:outline-none"
                            />
                            {field.units && (
                              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{field.units}</span>
                            )}
                          </>
                        ) : field.type === 'checkbox-list' ? (
                          <div className="flex flex-col gap-1">
                            {field.options.map((opt) => {
                              const checked = Array.isArray(gameRoomConfig[field.key])
                                ? gameRoomConfig[field.key].includes(opt.value)
                                : field.default?.includes(opt.value);
                              return (
                                <label key={opt.value} className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      let selected = Array.isArray(gameRoomConfig[field.key])
                                        ? [...gameRoomConfig[field.key]]
                                        : Array.isArray(field.default) ? [...field.default] : [];
                                      if (e.target.checked) {
                                        if (!selected.includes(opt.value)) selected.push(opt.value);
                                      } else {
                                        selected = selected.filter((v) => v !== opt.value);
                                      }
                                      const newCfg = { ...gameRoomConfig, [field.key]: selected };
                                      setGameRoomConfig(newCfg);
                                      onUpdateGameConfig && onUpdateGameConfig(newCfg);
                                    }}
                                  />
                                  <span>{opt.label}</span>
                                </label>
                              );
                            })}
                          </div>
                        ) : (
                          <>
                            {field.type === 'textWithLookup' ? (
                              <PickerTriggerButton
                                id={id}
                                label={gameRoomConfig[field.key] || field.placeholder || `Search ${field.lookupType || 'artist'}`}
                                onClick={() => setLookupField({ key: field.key, lookupType: field.lookupType || 'artist' })}
                                minWidth={140}
                                maxWidth={160}
                              />
                            ) : (
                            <input
                              id={id}
                              type={field.type}
                              value={gameRoomConfig[field.key]}
                              min={field.min}
                              max={field.max}
                              onChange={(e) => {
                                // Allow any value while typing
                                let val = e.target.value;
                                if (field.type === "number") val = e.target.value; // keep as string while typing
                                const newCfg = { ...gameRoomConfig, [field.key]: val };
                                setGameRoomConfig(newCfg);
                                onUpdateGameConfig && onUpdateGameConfig(newCfg);
                              }}
                              onBlur={(e) => {
                                // Clamp to min/max only on blur
                                let val = field.type === "number" ? Number(e.target.value) : e.target.value;
                                if (field.type === "number") {
                                  if (typeof field.min === "number" && val < field.min) val = field.min;
                                  if (typeof field.max === "number" && val > field.max) val = field.max;
                                }
                                const newCfg = { ...gameRoomConfig, [field.key]: val };
                                setGameRoomConfig(newCfg);
                                onUpdateGameConfig && onUpdateGameConfig(newCfg);
                              }}
                              className="border border-gray-300 dark:border-gray-600 px-3 py-0.5 rounded w-auto focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                              {...(field.type === 'number' && typeof field.max === 'number' ? {
                                size: String(field.max).length,
                                style: { minWidth: Math.max(48, String(field.max).length * 13) }
                              } : { style: { minWidth: 140 } })}
                            />
                            )}
                            {field.units && (
                              <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{field.units}</span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ));
          })()}
          {minMaxError && (
            <div className="text-red-600 dark:text-red-400 text-sm mb-2">
              {minMaxError}
            </div>
          )}
          {!isHost && !isSpectator && (
            <><div className="text-sm mb-1 text-gray-700 dark:text-gray-200">Are you ready to play?</div>
            <button
              type="button"
              className={`inline-block w-auto p-2 rounded mt-2 text-white text-sm font-semibold ${
                isReady
                  ? "bg-yellow-500 hover:bg-yellow-600"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
              style={{ minWidth: 0, width: "auto", display: "inline-block" }}
              onClick={onTogglePlayerReady}
            >
              {isReady ? "Unready" : "Ready"}
            </button>
            </>
          )}
        </form>
      );
    }
  }

  // Lookup modal rendering
  const handleLookupSelect = (item) => {
    if (!lookupField) return;
    const key = lookupField.key;
    // For artist lookup, store the artist name
    const val = item && item.name ? item.name : (item && item.id ? String(item.id) : '');
    const newCfg = { ...gameRoomConfig, [key]: val };
    setGameRoomConfig(newCfg);
    onUpdateGameConfig && onUpdateGameConfig(newCfg);
    setLookupField(null);
  };

  const handleLookupClose = () => setLookupField(null);

  return (
    <div className="">
      <ConfigUrlModal
        show={showConfigUrlModal}
        onClose={() => {
          setShowConfigUrlModal(false);
          setConfigUrlCopied(false);
        }}
        configUrl={configUrl}
        copied={configUrlCopied}
        onCopy={handleCopyConfigUrl}
      />
      <ConfigOptionPickerModal
        open={!!optionPickerField}
        title={optionPickerField ? optionPickerField.label : "Choose option"}
        options={optionPickerField?.options || []}
        selectedValue={optionPickerField?.selectedValue}
        onClose={() => setOptionPickerField(null)}
        onSelect={handleOptionPickerSelect}
      />
      {preservedView && (
        <div className="mb-3 p-2 rounded bg-yellow-50 dark:bg-yellow-900 border border-yellow-100 dark:border-yellow-800 flex items-center justify-between">
          <div className="text-sm text-yellow-800 dark:text-yellow-200">Editing current game settings (host-only). Changes will be saved as you edit.</div>
          <div>
            <button
              className="px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-sm"
              onClick={() => { try { if (typeof onClosePreservedView === 'function') onClosePreservedView(); } catch (e) {} }}
            >
              Back to Game
            </button>
          </div>
        </div>
      )}
      {/* Non-host ready button always visible at top */}
      <ReadyControl
        isHost={isHost}
        isSpectator={isSpectator}
        isReady={isReady}
        readyPlayers={gameRoomGame.readyPlayers || []}
        nonHostPlayers={nonHostPlayers}
        readyCount={readyCount}
        allReady={allReady}
        hostCanStartSolo={canHostStartSolo}
        onTogglePlayerReady={onTogglePlayerReady}
        onHostAction={() => {
          if (isHost && (requireReady ? allReady : true) && gameRoomPlayers.length >= minPlayers && !minMaxError) {
            onStartGame && onStartGame();
          }
        }}
        hostActionLabel={gameRoomPlayers.length < minPlayers ? `Start Game (needs ${playersRangeText(minPlayers, maxPlayers)} players)` : (requireReady ? (!allReady ? 'Waiting for players...' : 'Start Game') : 'Start Game (no ready required)')}
        hostActionDisabled={(requireReady && !allReady) || gameRoomPlayers.length < minPlayers || !!minMaxError}
        hostActionTitle={minMaxError ? minMaxError : requireReady && !allReady ? 'Waiting for all players to be ready' : gameRoomPlayers.length < minPlayers ? `Need ${minPlayers - gameRoomPlayers.length} more player(s)` : 'Start Game'}
      />
      <PlayerReadyList nonHostPlayers={nonHostPlayers} readyPlayers={gameRoomGame.readyPlayers || []} />

      {/* Tabs */}
      <div className="mt-6 border rounded-md bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 p-4">
        <div className="flex gap-2 mb-3">
          <button
            type="button"
            className={`px-3 py-1 font-semibold text-sm rounded ${tab === 0 ? 'border-b-2 border-blue-500 text-blue-700 dark:text-blue-300 bg-gray-100 dark:bg-gray-800' : 'hover:border-b-2 hover:border-blue-500 border-transparent text-gray-500 dark:text-gray-400 bg-transparent'}`}
            onClick={() => setTab(0)}
          >
            {lookupField && (
              <LookupModal
                open={!!lookupField}
                lookupType={lookupField.lookupType}
                onClose={handleLookupClose}
                onSelect={handleLookupSelect}
              />
            )}
            Setup
          </button>
          <button
            type="button"
            className={`px-3 py-1 font-semibold text-sm rounded ${tab === 1 ? 'border-b-2 border-blue-500 text-blue-700 dark:text-blue-300 bg-gray-100 dark:bg-gray-800' : 'hover:border-b-2 hover:border-blue-500 border-transparent text-gray-500 dark:text-gray-400 bg-transparent'}`}
            onClick={() => setTab(1)}
          >
            How to Play
          </button>
        </div>

        {/* Tab content */}
        <div className="pt-2">
          {tab === 0 && (
            <>
              {settingsSummary}
              {hostForm}
              {configUrl && (
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    className="text-blue-500 hover:underline bg-transparent border-none p-0 m-0 text-sm font-normal shadow-none focus:outline-none"
                    style={{ background: 'none', boxShadow: 'none', padding: 0, margin: 0 }}
                    onClick={() => setShowConfigUrlModal(true)}
                  >
                    View launch link
                  </button>
                </div>
              )}
            </>
          )}
          {tab === 1 && GuideComponent && (
            <div className="text-gray-800 dark:text-gray-200 rounded p-2 w-full min-h-[180px]">
              {isLazyComponent(GuideComponent)
                ? (
                    <Suspense fallback={<div>Loading guide...</div>}>
                      <GuideComponent />
                    </Suspense>
                  )
                : <GuideComponent />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GameRoomConfig;
